import classNames from "classnames";
import moment from "moment/moment";
import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { DatePicker } from "rsuite";

import { CalendarIcon } from "components/icons/CalendarIcon";
import style from "components/licenses/delivery-history/add-license-delivery.scss";
import { DeliveryMode } from "domain/licenses";
import { StoreState } from "store";
import form from "styles/form.scss";
import { acceptCorrectCalendarYear } from "utils/commonFunctions";
import {
    DATE_FORMAT,
    formatDatePickerValue,
    formatFormDateWithoutTime,
    generateTodayDate,
    parseDate,
} from "utils/format";

import testIds from "testIds.json";

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

interface Props {
    amount: number | null;
    expirationDate: string;
    onDateChange: (newDate: string) => void;
    dateString: string;
    onAmountChange: (newAmount: string) => void;
    showAmountType: boolean;
    onDeliveryModeChange: (newDeliveryMode: string) => void;
    deliveryMode: string;
    newDeal: boolean;
}

const GlobalSet = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    return (
        <div className={classNames(form.formFields)}>
            {props.showAmountType && (
                <label className={classNames(style.globalLabel)}>
                    {t("DeliveryHistory.addLicenseDelivery.amounts")}
                    <input
                        className={classNames(form.input, style.inputMargin, style.fixedWidth)}
                        type={"number"}
                        value={props.amount ?? ""}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => props.onAmountChange(event.target.value)}
                        data-testid={
                            testIds.workArea.license.deliveryHistory.createDeliveryDialog.globalAssignedAmountInput
                                .itself
                        }
                    />
                </label>
            )}

            <label className={classNames(style.globalLabel)}>
                {t("DeliveryHistory.addLicenseDelivery.expirationDates")}
                <DatePicker
                    format={DATE_FORMAT}
                    cleanable={false}
                    caretAs={(colorProps) => <CalendarIcon {...colorProps} color={props.theme.iconFillColor} />}
                    id={"expirationDate"}
                    className={classNames(form.dateRange, style.inputMargin, style.inputWidth)}
                    onChange={(date: Date) => {
                        if (date && !isNaN(date.getTime())) {
                            const value = acceptCorrectCalendarYear(date);
                            if (value != undefined) {
                                props.onDateChange(formatDatePickerValue(value as string));
                            }
                            props.onDateChange(formatDatePickerValue(value as string));
                        }
                    }}
                    disabledDate={(date: Date) => {
                        const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                        const lastDate = formatDatePickerValue(props.dateString);
                        return normalizedDate > moment(lastDate).toDate();
                    }}
                    value={parseDate(
                        formatFormDateWithoutTime(props.expirationDate ? props.expirationDate : generateTodayDate())
                    )}
                    data-testid={
                        testIds.workArea.license.deliveryHistory.createDeliveryDialog.globalExpirationDateInput.itself
                    }
                    renderValue={(value: Date) => {
                        return moment(value).format("DD/MM/YYYY");
                    }}
                />
            </label>
            {props.deliveryMode && (
                <label className={classNames(style.globalLabelDeliveryMode)}>
                    {t("DeliveryHistory.addLicenseDelivery.deliveryMode")}
                    <select
                        className={classNames(form.select, style.assignmentWidthInput, style.inputMargin)}
                        value={props.deliveryMode || DeliveryMode.ADD}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                            props.onDeliveryModeChange(event.target.value);
                        }}
                        data-testid={
                            testIds.workArea.license.deliveryHistory.createDeliveryDialog.globalDeliveryModeSelect
                                .itself
                        }
                    >
                        <option value={DeliveryMode.ADD}>{t("DeliveryHistory.addLicenseDelivery.table.add")}</option>
                        <option value={DeliveryMode.SET}>{t("DeliveryHistory.addLicenseDelivery.table.set")}</option>
                    </select>
                </label>
            )}
        </div>
    );
};
export default connector(GlobalSet);
